<template>
    <div class="cms-rich-text-editor">
        <div class="overflow-hidden rounded-md border border-gray-300 bg-white">
            <div class="flex flex-wrap items-stretch border-b border-gray-300 bg-white">
                <div class="min-w-0 flex-none border-r border-gray-300">
                    <select
                        v-model="activeFormatKey"
                        class="h-10 border-0 bg-transparent px-4 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-0"
                        @change="applyFormatSelection"
                    >
                        <option v-for="option in formatOptions" :key="option.key" :value="option.key">
                            {{ option.label }}
                        </option>
                    </select>
                </div>

                <div class="min-w-0 flex-none border-r border-gray-300">
                    <select
                        v-model="activeLineHeightKey"
                        class="h-10 border-0 bg-transparent px-4 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-0"
                        @change="applyLineHeightSelection"
                    >
                        <option v-for="option in lineHeightOptions" :key="option.key" :value="option.key">
                            {{ option.label }}
                        </option>
                    </select>
                </div>

                <div class="min-w-0 flex-none border-r border-gray-300">
                    <select
                        v-model="activeSpacingBeforeKey"
                        class="h-10 border-0 bg-transparent px-4 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-0"
                        @change="applySpacingBeforeSelection"
                    >
                        <option v-for="option in spacingBeforeOptions" :key="option.key" :value="option.key">
                            {{ option.label }}
                        </option>
                    </select>
                </div>

                <button
                    type="button"
                    class="inline-flex h-10 w-10 flex-none items-center justify-center border-r border-gray-300 bg-white text-gray-700 transition-colors duration-150 hover:bg-gray-50"
                    :class="toolbarButtonClass(isBoldActive)"
                    :title="t('Bold', 'Fett')"
                    @mousedown.prevent
                    @click="toggleInlineFormat('bold')"
                >
                    <span class="text-[1.05rem] font-semibold leading-none">B</span>
                </button>

                <button
                    type="button"
                    class="inline-flex h-10 w-10 flex-none items-center justify-center border-r border-gray-300 bg-white text-gray-700 transition-colors duration-150 hover:bg-gray-50"
                    :class="toolbarButtonClass(isItalicActive)"
                    :title="t('Italic', 'Kursiv')"
                    @mousedown.prevent
                    @click="toggleInlineFormat('italic')"
                >
                    <span class="text-[1.05rem] italic leading-none">I</span>
                </button>

                <button
                    type="button"
                    class="inline-flex h-10 w-10 flex-none items-center justify-center border-r border-gray-300 bg-white text-gray-700 transition-colors duration-150 hover:bg-gray-50"
                    :class="toolbarButtonClass(isUnderlineActive)"
                    :title="t('Underline', 'Unterstrichen')"
                    @mousedown.prevent
                    @click="toggleInlineFormat('underline')"
                >
                    <span class="text-[1.05rem] underline leading-none">U</span>
                </button>

                <button
                    type="button"
                    class="inline-flex h-10 w-10 flex-none items-center justify-center border-r border-gray-300 bg-white text-gray-700 transition-colors duration-150 hover:bg-gray-50"
                    :class="toolbarButtonClass(isBulletListActive)"
                    :title="t('Bullet list', 'Aufzählung')"
                    @mousedown.prevent
                    @click="toggleList('unordered')"
                >
                    <svg class="h-[1.05rem] w-[1.05rem]" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
                        <circle cx="4" cy="5" r="1.2" fill="currentColor" stroke="none"/>
                        <circle cx="4" cy="10" r="1.2" fill="currentColor" stroke="none"/>
                        <circle cx="4" cy="15" r="1.2" fill="currentColor" stroke="none"/>
                        <path d="M8 5h8M8 10h8M8 15h8" stroke-linecap="round"/>
                    </svg>
                </button>

                <button
                    type="button"
                    class="inline-flex h-10 w-10 flex-none items-center justify-center border-r border-gray-300 bg-white text-gray-700 transition-colors duration-150 hover:bg-gray-50"
                    :class="toolbarButtonClass(isNumberListActive)"
                    :title="t('Numbered list', 'Nummerierte Liste')"
                    @mousedown.prevent
                    @click="toggleList('ordered')"
                >
                    <svg class="h-[1.05rem] w-[1.05rem]" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
                        <path d="M2.5 5h1.5M2.5 10h1.5M2.5 15h1.5" stroke-linecap="round"/>
                        <path d="M8 5h8M8 10h8M8 15h8" stroke-linecap="round"/>
                    </svg>
                </button>

                <button
                    type="button"
                    class="inline-flex h-10 w-10 flex-none items-center justify-center border-r border-gray-300 bg-white text-gray-700 transition-colors duration-150 hover:bg-gray-50"
                    :class="toolbarButtonClass(isLinkActive)"
                    :title="t('Link', 'Link')"
                    @mousedown.prevent
                    @click="openLinkDialog"
                >
                    <svg-vue class="h-[1.05rem] w-[1.05rem]" icon="font-awesome.regular.link"/>
                </button>

                <button
                    type="button"
                    class="inline-flex h-10 w-10 flex-none items-center justify-center bg-white text-gray-700 transition-colors duration-150 hover:bg-gray-50"
                    :title="t('HTML', 'HTML')"
                    @mousedown.prevent
                    @click="openCodeDialog"
                >
                    <svg-vue class="h-[1.05rem] w-[1.05rem]" icon="font-awesome.regular.code"/>
                </button>
            </div>

            <tinymce
                :id="id"
                :model-value="modelValue"
                :init="editorConfig"
                @update:model-value="$emit('update:modelValue', $event)"
            />
        </div>
    </div>
</template>

<script>
import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/plugins/lists";
import "tinymce/plugins/code";
import "tinymce/plugins/link";
import "tinymce/plugins/autoresize";
import Editor from "@tinymce/tinymce-vue";
import {UI_COLORS} from '@/utils/ui-colors';

const BLOCK_SELECTOR = 'p,h1,h2,h3,h4,h5,h6';
const FONT_FAMILY_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const TEXT_VARIANT_OPTIONS = [
    {key: 'paragraph', label: {en: 'Format', de: 'Format'}, tag: 'p', classTokens: []},
    {key: 'h1', label: {en: 'H1', de: 'H1'}, tag: 'h1', classTokens: []},
    {key: 'h2', label: {en: 'H2', de: 'H2'}, tag: 'h2', classTokens: []},
    {key: 'h3', label: {en: 'H3', de: 'H3'}, tag: 'h3', classTokens: []},
    {key: 'h4', label: {en: 'H4', de: 'H4'}, tag: 'h4', classTokens: []},
    {key: 'h5', label: {en: 'H5', de: 'H5'}, tag: 'h5', classTokens: []},
    {key: 'h6', label: {en: 'H6', de: 'H6'}, tag: 'h6', classTokens: []},
    {key: 'text_large', label: {en: 'Large text', de: 'Schrift groß'}, tag: 'p', classTokens: ['text-lg', 'text-gray-700']},
    {key: 'text_standard', label: {en: 'Standard text', de: 'Standardtext'}, tag: 'p', classTokens: ['text-base', 'text-gray-700']},
    {key: 'text_small', label: {en: 'Small text', de: 'Standardtext klein'}, tag: 'p', classTokens: ['text-sm', 'text-gray-700']},
    {key: 'text_xsmall', label: {en: 'Very small text', de: 'Standardtext sehr klein'}, tag: 'p', classTokens: ['text-xs', 'text-gray-600']},
    {key: 'text_label', label: {en: 'Label text', de: 'Label-Text'}, tag: 'p', classTokens: ['text-xs', 'font-semibold', 'uppercase', 'tracking-wide', 'text-gray-500']},
];

const LINE_HEIGHT_OPTIONS = [
    {key: 'normal', label: {en: 'Line height', de: 'Zeilenabstand'}, classTokens: []},
    {key: 'small', label: {en: 'Small', de: 'Klein'}, classTokens: ['leading-6']},
    {key: 'medium', label: {en: 'Medium', de: 'Mittel'}, classTokens: ['leading-7']},
    {key: 'large', label: {en: 'Large', de: 'Groß'}, classTokens: ['leading-8']},
];

const SPACING_BEFORE_OPTIONS = [
    {key: 'normal', label: {en: 'Spacing before', de: 'Abstand davor'}, classTokens: []},
    {key: 'none', label: {en: 'No spacing', de: 'Kein Abstand'}, classTokens: ['mt-0']},
    {key: 'small', label: {en: 'Small', de: 'Klein'}, classTokens: ['mt-2']},
    {key: 'medium', label: {en: 'Medium', de: 'Mittel'}, classTokens: ['mt-4']},
    {key: 'large', label: {en: 'Large', de: 'Groß'}, classTokens: ['mt-8']},
];

const TEXT_VARIANT_CLASS_TOKENS = collectClassTokens(TEXT_VARIANT_OPTIONS);
const LINE_HEIGHT_CLASS_TOKENS = collectClassTokens(LINE_HEIGHT_OPTIONS);
const SPACING_BEFORE_CLASS_TOKENS = collectClassTokens(SPACING_BEFORE_OPTIONS);

function collectClassTokens(options) {
    return Array.from(new Set(
        options.flatMap((option) => Array.isArray(option.classTokens) ? option.classTokens : [])
    ));
}

function splitClasses(value) {
    return String(value || '')
        .split(/\s+/)
        .map((token) => token.trim())
        .filter(Boolean);
}

function normalizeClasses(tokens) {
    return Array.from(new Set(tokens.filter(Boolean))).join(' ');
}

function getClosestBlock(editor) {
    const node = editor.selection.getNode();

    return editor.dom.getParent(node, BLOCK_SELECTOR) || node;
}

function ensureEditableBlocks(editor) {
    const selectedBlocks = editor.selection.getSelectedBlocks();
    const blocks = (selectedBlocks && selectedBlocks.length > 0)
        ? selectedBlocks
        : [getClosestBlock(editor)];

    return blocks.map((block) => {
        const tagName = String(block?.nodeName || '').toLowerCase();

        if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
            return block;
        }

        editor.execCommand('FormatBlock', false, 'p');

        return getClosestBlock(editor);
    });
}

function hasAllTokens(block, tokens) {
    if (!tokens.length) {
        return false;
    }

    const classTokens = splitClasses(block?.getAttribute?.('class'));

    return tokens.every((token) => classTokens.includes(token));
}

function getCurrentFormatKey(editor) {
    const block = getClosestBlock(editor);
    const tagName = String(block?.nodeName || '').toLowerCase();

    if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
        return tagName;
    }

    const match = TEXT_VARIANT_OPTIONS.find((option) => option.key.startsWith('text_') && hasAllTokens(block, option.classTokens));

    return match?.key || 'paragraph';
}

function getCurrentGroupKey(editor, options) {
    const block = getClosestBlock(editor);
    const match = options.find((option) => hasAllTokens(block, option.classTokens));

    return match?.key || 'normal';
}

function applyFormatOption(editor, option) {
    editor.undoManager.transact(() => {
        ensureEditableBlocks(editor).forEach((block) => {
            const currentTag = String(block?.nodeName || '').toLowerCase();
            const target = currentTag !== option.tag
                ? editor.dom.rename(block, option.tag)
                : block;

            const normalizedClassTokens = splitClasses(target.getAttribute('class'))
                .filter((token) => !TEXT_VARIANT_CLASS_TOKENS.includes(token))
                .concat(option.classTokens);

            const className = normalizeClasses(normalizedClassTokens);

            if (className === '') {
                target.removeAttribute('class');
            } else {
                editor.dom.setAttrib(target, 'class', className);
            }
        });
    });
}

function applyClassGroupOption(editor, option, groupTokens) {
    editor.undoManager.transact(() => {
        ensureEditableBlocks(editor).forEach((block) => {
            const normalizedClassTokens = splitClasses(block.getAttribute('class'))
                .filter((token) => !groupTokens.includes(token))
                .concat(option.classTokens);

            const className = normalizeClasses(normalizedClassTokens);

            if (className === '') {
                block.removeAttribute('class');
            } else {
                editor.dom.setAttrib(block, 'class', className);
            }
        });
    });
}

function buildEditorContentStyle() {
    return `
        html,
        body,
        body#tinymce,
        body.mce-content-body,
        .mce-content-body,
        .cms-rich-text-body,
        .cms-rich-text-body * {
            font-family: ${FONT_FAMILY_STACK} !important;
            color: ${UI_COLORS.text.heading} !important;
            background: #ffffff !important;
        }
        body,
        body#tinymce,
        body.mce-content-body,
        .cms-rich-text-body {
            font-size: 16px !important;
            line-height: 1.75 !important;
            margin: 0 !important;
            padding: 1rem !important;
        }
        p,
        body p,
        .cms-rich-text-body p {
            margin: 0 0 1rem !important;
            font-size: 1rem !important;
            line-height: 1.75 !important;
            color: ${UI_COLORS.text.secondary} !important;
        }
        h1, h2, h3, h4, h5, h6 {
            margin: 0 0 1rem !important;
            font-weight: 600 !important;
            color: ${UI_COLORS.text.heading} !important;
            letter-spacing: -0.025em !important;
        }
        h1 { font-size: 2.25rem !important; line-height: 1.1 !important; }
        h2 { font-size: 1.875rem !important; line-height: 1.15 !important; }
        h3 { font-size: 1.5rem !important; line-height: 1.25 !important; }
        h4 { font-size: 1.25rem !important; line-height: 1.4 !important; }
        h5 { font-size: 1.125rem !important; line-height: 1.5 !important; }
        h6 { font-size: 1rem !important; line-height: 1.75 !important; }
        ul, ol {
            margin: 0 0 1rem 1.25rem !important;
            padding: 0 !important;
        }
        li {
            margin: 0.2rem 0 !important;
        }
        p.text-lg,
        body p.text-lg,
        .cms-rich-text-body p.text-lg,
        .text-lg {
            font-size: 1.25rem !important;
            line-height: 1.9 !important;
        }
        p.text-base,
        body p.text-base,
        .cms-rich-text-body p.text-base,
        .text-base {
            font-size: 1rem !important;
            line-height: 1.75 !important;
        }
        p.text-sm,
        body p.text-sm,
        .cms-rich-text-body p.text-sm,
        .text-sm {
            font-size: 0.875rem !important;
            line-height: 1.6 !important;
        }
        p.text-xs,
        body p.text-xs,
        .cms-rich-text-body p.text-xs,
        .text-xs {
            font-size: 0.75rem !important;
            line-height: 1.55 !important;
        }
        p.text-gray-700,
        body p.text-gray-700,
        .cms-rich-text-body p.text-gray-700,
        .text-gray-700 {
            color: ${UI_COLORS.text.secondary} !important;
        }
        p.text-gray-600,
        body p.text-gray-600,
        .cms-rich-text-body p.text-gray-600,
        .text-gray-600 {
            color: ${UI_COLORS.text.default} !important;
        }
        p.text-gray-500,
        body p.text-gray-500,
        .cms-rich-text-body p.text-gray-500,
        .text-gray-500 {
            color: ${UI_COLORS.text.muted} !important;
        }
        p.font-semibold,
        body p.font-semibold,
        .cms-rich-text-body p.font-semibold,
        .font-semibold {
            font-weight: 600 !important;
        }
        p.uppercase,
        body p.uppercase,
        .cms-rich-text-body p.uppercase,
        .uppercase {
            text-transform: uppercase !important;
        }
        p.tracking-wide,
        body p.tracking-wide,
        .cms-rich-text-body p.tracking-wide,
        .tracking-wide {
            letter-spacing: 0.12em !important;
        }
        p.leading-6,
        body p.leading-6,
        .cms-rich-text-body p.leading-6,
        .leading-6 {
            line-height: 1.5rem !important;
        }
        p.leading-7,
        body p.leading-7,
        .cms-rich-text-body p.leading-7,
        .leading-7 {
            line-height: 1.75rem !important;
        }
        p.leading-8,
        body p.leading-8,
        .cms-rich-text-body p.leading-8,
        .leading-8 {
            line-height: 2rem !important;
        }
        .mt-0 { margin-top: 0 !important; }
        .mt-2 { margin-top: 0.5rem !important; }
        .mt-4 { margin-top: 1rem !important; }
        .mt-8 { margin-top: 2rem !important; }
        strong, b {
            font-weight: 600 !important;
        }
        em, i {
            font-style: italic !important;
        }
        u {
            text-decoration: underline !important;
        }
        a {
            color: ${UI_COLORS.text.heading} !important;
            text-decoration: underline !important;
        }
    `;
}

export default {
    name: "CmsRichTextEditor",
    components: {
        tinymce: Editor,
    },
    props: {
        modelValue: {
            type: String,
            default: '',
        },
        id: {
            type: String,
            required: true,
        },
        minHeight: {
            type: Number,
            default: 320,
        },
    },
    emits: ['update:modelValue'],
    data() {
        const isGerman = String(document.documentElement.lang || '').toLowerCase().startsWith('de');
        const t = (en, de) => isGerman ? de : en;
        const self = this;

        return {
            editor: null,
            activeFormatKey: 'paragraph',
            activeLineHeightKey: 'normal',
            activeSpacingBeforeKey: 'normal',
            isBoldActive: false,
            isItalicActive: false,
            isUnderlineActive: false,
            isBulletListActive: false,
            isNumberListActive: false,
            isLinkActive: false,
            formatOptions: TEXT_VARIANT_OPTIONS.map((option) => ({
                ...option,
                label: isGerman ? option.label.de : option.label.en,
            })),
            lineHeightOptions: LINE_HEIGHT_OPTIONS.map((option) => ({
                ...option,
                label: isGerman ? option.label.de : option.label.en,
            })),
            spacingBeforeOptions: SPACING_BEFORE_OPTIONS.map((option) => ({
                ...option,
                label: isGerman ? option.label.de : option.label.en,
            })),
            editorConfig: {
                branding: false,
                menubar: false,
                statusbar: false,
                toolbar: false,
                browser_spellcheck: true,
                convert_urls: false,
                content_css: false,
                content_style: buildEditorContentStyle(),
                body_class: 'cms-rich-text-body',
                forced_root_block: 'p',
                element_format: 'html',
                default_link_target: '_blank',
                link_assume_external_targets: true,
                target_list: false,
                link_title: false,
                plugins: 'lists code link autoresize',
                min_height: this.minHeight,
                autoresize_min_height: this.minHeight,
                valid_elements: 'p[class],h1[class],h2[class],h3[class],h4[class],h5[class],h6[class],ul[class],ol[class],li[class],strong/b,em/i,u,a[href|target|rel|class],br',
                setup(editor) {
                    const syncToolbarState = function () {
                        self.syncToolbarState(editor);
                    };

                    editor.on('init', function () {
                        self.editor = editor;
                        self.applyEditorContentStyle(editor);
                        syncToolbarState();
                    });

                    editor.on('NodeChange Change SetContent KeyUp Undo Redo', syncToolbarState);
                },
            },
            translate: t,
        };
    },
    methods: {
        t(en, de) {
            return this.translate(en, de);
        },
        toolbarButtonClass(isActive) {
            return {
                'bg-gray-50 text-gray-900': isActive,
                'text-gray-700 hover:bg-gray-50': !isActive,
            };
        },
        applyEditorContentStyle(editor) {
            const doc = editor?.getDoc?.();

            if (!doc) {
                return;
            }

            let styleElement = doc.getElementById('cms-rich-text-content-style');

            if (!styleElement) {
                styleElement = doc.createElement('style');
                styleElement.id = 'cms-rich-text-content-style';
                doc.head.appendChild(styleElement);
            }

            styleElement.textContent = buildEditorContentStyle();
            doc.documentElement.style.fontFamily = FONT_FAMILY_STACK;
            doc.body.style.fontFamily = FONT_FAMILY_STACK;
            doc.body.style.color = UI_COLORS.text.heading;
        },
        syncToolbarState(editor) {
            if (!editor) {
                return;
            }

            this.activeFormatKey = getCurrentFormatKey(editor);
            this.activeLineHeightKey = getCurrentGroupKey(editor, this.lineHeightOptions);
            this.activeSpacingBeforeKey = getCurrentGroupKey(editor, this.spacingBeforeOptions);
            this.isBoldActive = editor.formatter.match('bold');
            this.isItalicActive = editor.formatter.match('italic');
            this.isUnderlineActive = editor.formatter.match('underline');
            this.isBulletListActive = editor.queryCommandState('InsertUnorderedList');
            this.isNumberListActive = editor.queryCommandState('InsertOrderedList');

            const node = editor.selection.getNode();
            this.isLinkActive = !!editor.dom.getParent(node, 'a[href]');
        },
        focusEditor() {
            if (this.editor) {
                this.editor.focus();
            }
        },
        applyFormatSelection() {
            if (!this.editor) {
                return;
            }

            const option = this.formatOptions.find((entry) => entry.key === this.activeFormatKey);

            if (!option) {
                return;
            }

            this.focusEditor();
            applyFormatOption(this.editor, option);
            this.syncToolbarState(this.editor);
        },
        applyLineHeightSelection() {
            if (!this.editor) {
                return;
            }

            const option = this.lineHeightOptions.find((entry) => entry.key === this.activeLineHeightKey);

            if (!option) {
                return;
            }

            this.focusEditor();
            applyClassGroupOption(this.editor, option, LINE_HEIGHT_CLASS_TOKENS);
            this.syncToolbarState(this.editor);
        },
        applySpacingBeforeSelection() {
            if (!this.editor) {
                return;
            }

            const option = this.spacingBeforeOptions.find((entry) => entry.key === this.activeSpacingBeforeKey);

            if (!option) {
                return;
            }

            this.focusEditor();
            applyClassGroupOption(this.editor, option, SPACING_BEFORE_CLASS_TOKENS);
            this.syncToolbarState(this.editor);
        },
        toggleInlineFormat(format) {
            if (!this.editor) {
                return;
            }

            this.focusEditor();
            this.editor.execCommand(format.charAt(0).toUpperCase() + format.slice(1));
            this.syncToolbarState(this.editor);
        },
        toggleList(type) {
            if (!this.editor) {
                return;
            }

            this.focusEditor();
            this.editor.execCommand(type === 'ordered' ? 'InsertOrderedList' : 'InsertUnorderedList');
            this.syncToolbarState(this.editor);
        },
        openLinkDialog() {
            if (!this.editor) {
                return;
            }

            this.focusEditor();
            this.editor.execCommand('mceLink');
        },
        openCodeDialog() {
            if (!this.editor) {
                return;
            }

            this.focusEditor();
            this.editor.execCommand('mceCodeEditor');
        },
    },
};
</script>

<style scoped>
.cms-rich-text-toolbar-button {
    display: inline-flex;
    height: 2.5rem;
    width: 2.5rem;
    align-items: center;
    justify-content: center;
    border-right: 1px solid rgb(229, 231, 235);
    background: transparent;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.cms-rich-text-editor :deep(.tox-tinymce) {
    border: 0 !important;
    border-radius: 0 !important;
}

.cms-rich-text-editor :deep(.tox .tox-editor-header) {
    display: none !important;
}

.cms-rich-text-editor :deep(.tox .tox-edit-area__iframe) {
    background: #ffffff !important;
}
.cms-rich-text-editor :deep(select) {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
}
</style>
